import { ErrorResponse, ErrorTimeout, User, type UserManager } from 'oidc-client-ts';

import { dispatchInteractiveReauthRequired } from './auth-events';

const REAUTH_LOGIN_HINT_KEY = 'inflo:reauth-login-hint';

let recoveryInstalled = false;

/**
 * When many callers run silent renew at once (storage/hooks/API), only one
 * `signinSilent` runs; others await the same promise. Matches prior useAuthState behavior.
 */
let signinSilentCoalesce: Promise<void> | null = null;

/**
 * When Inflo APIs reject the bearer token, parallel requests all await one recovery
 * (silent refresh first, then interactive re-auth only if still invalid).
 */
let infloTokenDenialRecovery: Promise<'ok' | 'reauth'> | null = null;

/** Coalesces legacy API callers that only need a forced re-login (no silent attempt). */
let infloForceReauthCoalesce: Promise<void> | null = null;

function readSessionStorageItem(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionStorageItem(key: string, value: string | null): void {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, value);
    }
  } catch {
    // ignore
  }
}

export function readStoredReauthLoginHint(): string | undefined {
  const storedHint = readSessionStorageItem(REAUTH_LOGIN_HINT_KEY);
  return storedHint ? storedHint : undefined;
}

export function clearStoredReauthLoginHint(): void {
  writeSessionStorageItem(REAUTH_LOGIN_HINT_KEY, null);
}

function persistReauthLoginHint(hint: string | undefined): void {
  if (!hint) {
    writeSessionStorageItem(REAUTH_LOGIN_HINT_KEY, null);
  } else {
    writeSessionStorageItem(REAUTH_LOGIN_HINT_KEY, hint);
  }
}

function getLoginHintFromUserProfileData(values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return undefined;
}

export function loginHintFromUser(user: User | null | undefined): string | undefined {
  if (!user?.profile) {
    return undefined;
  }
  const userProfile = user.profile as Record<string, unknown>;
  return getLoginHintFromUserProfileData([
    userProfile.preferred_username,
    userProfile.email,
    userProfile.login_hint,
    userProfile.upn,
  ]);
}

export function isOAuthSessionInvalidError(error: unknown): boolean {
  if (error instanceof ErrorResponse) {
    const code = (error.error ?? '').toLowerCase();
    return (
      code === 'invalid_grant' ||
      code === 'invalid_token' ||
      code === 'login_required' ||
      code === 'interaction_required' ||
      code === 'consent_required'
    );
  }
  const message = String((error as Error)?.message ?? error);
  return /invalid_grant|login_required|interaction_required|consent_required|invalid_token/i.test(message);
}

/**
 * Inflo API returns this shape when the bearer token is no longer accepted (even if the SPA still has a user object).
 * Match on `Message` only (case-insensitive), per product convention.
 */
export function isInfloApiUnauthorizedResponseBody(body: unknown): boolean {
  if (!body || typeof body !== 'object') {
    return false;
  }
  const msg = (body as Record<string, unknown>).Message;
  if (typeof msg !== 'string') {
    return false;
  }
  return msg.trim().toLowerCase() === 'unauthorized access';
}

async function performInteractiveSessionReset(userManager: UserManager): Promise<void> {
  let user: User | null = null;
  try {
    user = await userManager.getUser();
  } catch {
    user = null;
  }

  const hint = loginHintFromUser(user);
  persistReauthLoginHint(hint);

  try {
    await userManager.removeUser();
  } catch {
    // ignore
  }

  dispatchInteractiveReauthRequired(hint);
}

/**
 * Clears the invalid session and prompts for interactive login, reusing profile hints when possible.
 */
export async function recoverFromInvalidSession(userManager: UserManager, error: unknown): Promise<boolean> {
  if (!isOAuthSessionInvalidError(error)) {
    return false;
  }

  await performInteractiveSessionReset(userManager);
  return true;
}

/**
 * Same as `oidc` `userManager.signinSilent()` with shared error handling (invalid_grant, etc. →
 * `recoverFromInvalidSession`). A single in-flight `signinSilent` is shared with hooks, automatic
 * token timers, and API recovery so we do not stampede the token / userinfo endpoints.
 */
export async function coalescedUserManagerSigninSilent(userManager: UserManager): Promise<void> {
  if (signinSilentCoalesce) {
    return signinSilentCoalesce;
  }
  const pending = (async () => {
    const attemptSilent = async () => {
      await userManager.signinSilent();
    };
    try {
      await attemptSilent();
    } catch (error) {
      if (error instanceof ErrorTimeout) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          await attemptSilent();
        } catch (retryErr) {
          await recoverFromInvalidSession(userManager, retryErr);
        }
        return;
      }
      await recoverFromInvalidSession(userManager, error);
    }
  })();
  signinSilentCoalesce = pending;
  try {
    await pending;
  } finally {
    signinSilentCoalesce = null;
  }
}

/**
 * When an Inflo API call fails because the access token is stale (e.g. after sleep, `automaticSilentRenew`
 * may not have run in time), try a silent `signinSilent` first. Only if we still have no valid token,
 * clear the session and require interactive re-login (login hint already handled by
 * `performInteractiveSessionReset`).
 */
export function resolveInfloApiTokenDenial(userManager: UserManager): Promise<'ok' | 'reauth'> {
  if (infloTokenDenialRecovery) {
    return infloTokenDenialRecovery;
  }
  infloTokenDenialRecovery = (async (): Promise<'ok' | 'reauth'> => {
    await coalescedUserManagerSigninSilent(userManager);

    const user = await userManager.getUser();
    if (user && !user.expired && user.access_token) {
      return 'ok';
    }
    if (!user) {
      // `recoverFromInvalidSession` during silent renew already removed the user and dispatched re-auth
      return 'reauth';
    }
    await performInteractiveSessionReset(userManager);
    return 'reauth';
  })();
  return infloTokenDenialRecovery.finally(() => {
    infloTokenDenialRecovery = null;
  });
}

/**
 * If a retried call still cannot authenticate after `resolveInfloApiTokenDenial` returned "ok", force
 * interactive re-login (do not run another silent attempt).
 */
export function reauthInfloSessionInteractivelyAfterRetryFailed(userManager: UserManager): Promise<void> {
  if (infloForceReauthCoalesce) {
    return infloForceReauthCoalesce;
  }
  infloForceReauthCoalesce = (async () => {
    await performInteractiveSessionReset(userManager);
  })();
  return infloForceReauthCoalesce.finally(() => {
    infloForceReauthCoalesce = null;
  });
}

/**
 * Subscribes to silent-renew failures so expired refresh tokens trigger re-authentication, and
 * registers token lifetime hooks to call the same coalesced `signinSilent` used elsewhere (avoids
 * duplicate renews when `automaticSilentRenew` would also fire).
 */
export function installAuthSessionRecovery(userManager: UserManager): void {
  if (recoveryInstalled) {
    return;
  }
  recoveryInstalled = true;

  const renewSilently = () => {
    void coalescedUserManagerSigninSilent(userManager);
  };
  userManager.events.addAccessTokenExpiring(renewSilently);
  userManager.events.addAccessTokenExpired(renewSilently);

  userManager.events.addSilentRenewError((error) => {
    void recoverFromInvalidSession(userManager, error);
  });
}
